import { useQuery } from '@tanstack/react-query';
import { GraphQLClient } from 'graphql-request';
import { useState, useCallback } from 'react';
import { RuntimeConfiguration } from '@/configuration';
import { GET_CONVERSION_RATE } from '@/graphQl/nftList';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { NetlifyProps } from '../interface';
import { ConversionData } from '../providers/NftContextProvider';
import { decryptLocalData, encryptLocalData } from '../utils/encryptAndDecrypt';

export type CurrencyRateProps = NetlifyProps & {
  response: ConversionData;
};

export interface CurrencyRate {
  answerUpdateds: AnswerUpdated[];
}

export interface AnswerUpdated {
  current: string;
  updatedAt: string;
}

export const useCurrencyRate = () => {
  const [priceData, setPriceData] = useState<number | string | undefined>();

  const getCurrencyRate = useCallback(async () => {
    const lastCallTime = decryptLocalData(STORAGE_KEYS.API_CALL_TIME);
    const currentTime = Date.now();
    const isWithin60Seconds =
      lastCallTime && currentTime - Number(lastCallTime) < Number(RuntimeConfiguration.API_CALL_TIME);

    if (isWithin60Seconds) {
      // Fetch priceData from localStorage if a call is ignored
      const storedPriceData = decryptLocalData(STORAGE_KEYS.PRICE_DATA);

      if (storedPriceData) {
        setPriceData(storedPriceData);
      }

      return null; // Exit the function early
    }

    try {
      const clientServiceTwo = new GraphQLClient(RuntimeConfiguration.SUBGRAPH_CURRENCY_API);
      const currencyData = await clientServiceTwo.request<CurrencyRate>(GET_CONVERSION_RATE);
      const ethValue = Number(currencyData.answerUpdateds[0].current) / 10 ** 8;

      encryptLocalData(STORAGE_KEYS.PRICE_DATA, JSON.stringify(ethValue));
      encryptLocalData(STORAGE_KEYS.API_CALL_TIME, String(currentTime));
      setPriceData(ethValue);
    } catch (error) {
      throw error;
    }
  }, []);

  useQuery({
    queryKey: ['currency_rate'],
    queryFn: async () => {
      await getCurrencyRate();

      return null;
    }
  });

  return { priceData, getCurrencyRate };
};
